import { useState, useMemo, useCallback } from "react";
import { useSearchParams } from "react-router";
import { Disclosure, DisclosureButton } from "@headlessui/react";
import { useToast } from "../../../../../components/toast/useToast.shared";
import { SlideTransition } from "../../../../../components/transition/SlideTransition";
import { Toast } from "../../../../../components/toast/Toast";
import { SpinnerLegacy } from "../../../../../components";
import { Alert } from "../../../../../components/alert/Alert";
import {
  Dialog,
  DialogNavigation,
  DialogContent,
  DialogCTA,
} from "../../../../../components/modal/Dialog";
import {
  RadioGroup,
  RadioGroupOption,
  RadioGroupContainer,
} from "../../../../../components/radioSelector/RadioGroup";
import { Button, InputText } from "@sellar-io/components";
import { useQueryClient } from "@tanstack/react-query";
import { apiV2AssertError } from "../../../../../hooks/api/v2/apiV2Request";
import { useProductStorefrontTagAssign } from "../../../../../hooks/api/v2/productStorefrontTag/useProductStorefrontTagAssign";
import { useStorefrontTagList } from "../../../../../hooks/api/v2/storefrontTags/useStorefrontTagList";
import { useStorefrontTagCreate } from "../../../../../hooks/api/v2/storefrontTags/useStorefrontTagCreate";
import { useSellarCurrentBusiness } from "../../../../../hooks/useSellarCurrentBusiness.shared";
import { useFirebaseUser } from "../../../../../hooks/useFirebaseUser.shared";
import { ServerStateKeys } from "../../../../../hooks/api/v2/ServerStateKeys";
import { Icon, H5 } from "@sellar-io/components";
export const StorefrontAssignProductTagModal = ({
  active,
  onDismiss,
  productId,
  productName,
}: {
  active: boolean;
  onDismiss: () => void;
  productId: number;
  productName: string;
}) => {
  const [dismissAttempts, setDismissAttempts] = useState(0);
  const [newTagName, setNewTagName] = useState("");
  const [createMessage, setCreateMessage] = useState<string>();
  const [selectedStorefrontTagId, setSelectedStorefrontTagId] = useState<
    number | null
  >(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const { business } = useSellarCurrentBusiness();
  const { user } = useFirebaseUser();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // TODO: maybe when it becomes this complicated we should stick this in zustand?
  const reset = useCallback(() => {
    setDismissAttempts(0);
    setSelectedStorefrontTagId(null);
    setNewTagName("");
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("new");
    setSearchParams(nextParams, { replace: true });
  }, [searchParams, setSearchParams]);

  const queryKeysToInvalidate = useMemo(
    () =>
      [
        [user?.uid, business?.id, ServerStateKeys.StorefrontTags],
        [user?.uid, business?.id, ServerStateKeys.Products, productId],
        [user?.uid, business?.id, ServerStateKeys.UngroupedProducts],
      ] as const,
    [user?.uid, business?.id, productId],
  );

  const onSuccessfulMove = useCallback(
    (groupName: string, toastDelay = 1000) => {
      queryKeysToInvalidate.forEach((key) =>
        queryClient.invalidateQueries({ queryKey: key }),
      );

      onDismiss();
      reset();

      toast({
        placement: "bottom",
        delay: toastDelay,
        render: () => (
          <Toast icon="CHECK_CIRCLE">
            {`This product has been added to group: ${groupName}`}
          </Toast>
        ),
      });
    },
    [queryClient, queryKeysToInvalidate, onDismiss, reset, toast],
  );

  // existing‐tag assign
  const assignTag = useProductStorefrontTagAssign();
  const tagsQuery = useStorefrontTagList(
    {
      query: [
        {
          type: "SupplierId",
          value: business?.Supplier?.id ?? 0,
          modifier: "EQUALS",
        },
      ],
    },
    { enabled: !!business?.Supplier?.id },
  );

  // inline create
  const createTag = useStorefrontTagCreate();

  // first‐time “close” warning
  const handleDismiss = () => {
    if (dismissAttempts === 0) {
      setDismissAttempts(1);
    } else {
      reset();
      onDismiss();
    }
  };
  const showWarning = dismissAttempts > 0;

  const handleSubmit = () => {
    if (selectedStorefrontTagId != null) {
      handleAssign();
    } else if (newTagName) {
      handleCreateAndAssign();
    }
  };

  const handleAssign = () => {
    if (!selectedStorefrontTagId) return;
    const groupName =
      tagsQuery.data?.pages[0]?.find((t) => t.id === selectedStorefrontTagId)
        ?.value ?? "";

    assignTag.mutate(
      { StorefrontTagId: selectedStorefrontTagId, ProductId: productId },
      {
        onSuccess: () => onSuccessfulMove(groupName),
      },
    );
  };

  const handleCreateAndAssign = () => {
    const trimmed = newTagName.trim();
    if (!trimmed) {
      setCreateMessage("Please enter a group name.");
      return;
    }
    setCreateMessage(undefined);

    createTag.mutate(
      { value: trimmed, ProductIds: [productId] },
      {
        onSuccess: () => onSuccessfulMove(trimmed),
        onError: (err) =>
          setCreateMessage(
            apiV2AssertError(err)?.response?.data.message ?? "Unknown error",
          ),
      },
    );
  };

  return (
    <Dialog active={active} onDismiss={handleDismiss}>
      <DialogNavigation
        leftIcon="CHEVRON_DOWN"
        onClickLeftIcon={handleDismiss}
        title={`Add ${productName} to a group`}
        description={"Organise your storefront by adding products to a group"}
      />

      <DialogContent>
        {(assignTag.isPending || tagsQuery.isLoading) && <SpinnerLegacy />}
        {!assignTag.isPending &&
          !tagsQuery.isLoading &&
          (tagsQuery.data?.pages[0]?.length ?? 0) > 0 && (
            <>
              {assignTag.isError && (
                <Alert
                  message={
                    apiV2AssertError(assignTag.error)?.response?.data.message ??
                    "Unknown error"
                  }
                />
              )}

              <RadioGroup
                value={
                  selectedStorefrontTagId != null
                    ? selectedStorefrontTagId.toString()
                    : ""
                }
                onChange={(val) => {
                  setSelectedStorefrontTagId(Number(val));
                  setNewTagName("");
                }}
              >
                <H5 className="mb-2">Available Groups</H5>
                <RadioGroupContainer>
                  {tagsQuery.data?.pages[0]?.map((tag) => (
                    <RadioGroupOption key={tag.id} value={tag.id.toString()}>
                      {tag.value}
                    </RadioGroupOption>
                  ))}
                </RadioGroupContainer>
              </RadioGroup>
            </>
          )}

        <div className="mt-4 flex flex-1 flex-col rounded-lg border border-zinc-300 bg-white px-2 py-4">
          <Disclosure as="div">
            {({ open }) => (
              <>
                <DisclosureButton className="flex w-full cursor-pointer items-center gap-2">
                  <Icon size={1} icon={open ? "MINUS" : "PLUS"} />
                  <span className="text-sm font-medium">
                    Create a new group
                  </span>
                </DisclosureButton>
                <SlideTransition
                  show={open}
                  parentClass="duration-200 ease-out"
                >
                  <div className="my-4">
                    {createMessage && (
                      <Alert type="warning" message={createMessage} />
                    )}
                    <div className="flex flex-col gap-2">
                      <InputText
                        value={newTagName}
                        onChange={(e) => {
                          setNewTagName(e.target.value);
                          setSelectedStorefrontTagId(null);
                        }}
                        placeholder="New group name"
                      />
                      <span className="flex items-center gap-1 text-zinc-500">
                        <Icon
                          size={0.8}
                          icon={"INFORMATION_OUTLINE"}
                          color={"text-zinc-500"}
                        />
                        Your product will be added to this group
                      </span>
                    </div>
                  </div>
                </SlideTransition>
              </>
            )}
          </Disclosure>
        </div>

        {showWarning && (
          <Alert
            message="You haven’t added this product to a group"
            type="warning"
            className="mx-0 my-4"
          />
        )}
      </DialogContent>

      <DialogCTA>
        <Button
          onClick={() => handleDismiss()}
          disabled={assignTag.isPending}
          color="transparent"
        >
          Dismiss
        </Button>
        <Button
          onClick={() => handleSubmit()}
          disabled={
            (!selectedStorefrontTagId && !newTagName) || assignTag.isPending
          }
        >
          Add
        </Button>
      </DialogCTA>
    </Dialog>
  );
};
